import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen } from 'lucide-react';

const ASSETS_URL = '/api';

const AboutPage = ({ onOpenAppMenu }) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 font-['HelveticaRegular']">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={onOpenAppMenu}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Open menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <div className="flex items-center gap-2">
                <BookOpen className="text-blue-600" size={24} />
                <h1 className="text-xl font-bold text-gray-900 font-['bpg-caps']">
                  აფხაზური ენის კურსი
                </h1>
              </div>
            </div>
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft size={16} />
              <span className="font-['bpg-caps']">მთავარი</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Title */}
          <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center font-['bpg-caps']">
            კურსის შესახებ
          </h1>

          {/* Project Description */}
          <section className="mb-8">
            <p className="text-gray-700 leading-relaxed mb-4">
              საგრანტო პროექტის <strong>„აფხაზური ენის ელექტრონული სწავლების კურსი (A1 დონე)"</strong> მიზანი იყო აფხაზური ენის A1 დონის თანამედროვე, ყველასათვის ხელმისაწვდომი და ეფექტური ელექტრონული სასწავლო კურსის შექმნა. ეს შეესაბამება საქართველოს სახელმწიფო ენის ათწლიან სტრატეგიას, რომლის ერთ-ერთი ამოცანაა აფხაზური ენის ფუნქციონირებისა და პოპულარიზაციის მხარდაჭერა, რადგანაც, საქართველოს კონსტიტუციით, აფხაზური სახელმწიფო ენაა აფხაზეთის ავტონომიურ რესპუბლიკაში, ქართულთან ერთად.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              პროექტის ფარგლებში შექმნილი ელექტრონული კურსი დაინტერესებულ პირებს საშუალებას აძლევს, სახლიდან გაუსვლელად, მათთვის მოსახერხებელ დროსა და ადგილას, სწავლის, სწავლებისა და შეფასების საერთო ევროპულ ჩარჩოსთან (CEFR) მისადაგებულად, საწყის დონეზე (A1) შეისწავლონ აფხაზური ენა როგორც კომპიუტერით, ისე მობილური ტელეფონით.
            </p>
            <p className="text-gray-700 leading-relaxed">
              კურსი დახმარებას გაუწევს ენის შემსწავლელებს, გაზრდის ინფორმირებულობას აფხაზური ენის, ხალხისა და კულტურისადმი, ქართველ ხალხს უკეთ გააცნობს აფხაზებსა და აფხაზურ ენას, გააჩენს დადებით დამოკიდებულებას მათდამი, გარკვეულ წვლილს შეიტანს ამ ენის პრესტიჟის ამაღლებაში. ამდენად, პროექტი მნიშვნელოვანია სტრატეგიული, ინოვაციური მიდგომების დანერგვის, ციფრული პედაგოგიკის განვითარების, კვლევითი რესურსის გაჩენისა და საზოგადოებრივი ინტეგრაციის განვითარების თვალსაზრისით.
            </p>
          </section>

          {/* Organizations */}
          <section className="mb-8 bg-gray-50 rounded-lg p-6">
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <img src={`${ASSETS_URL}/images/enagrami-logo.png`} alt="ენაგრამი" className="w-16 h-auto" onError={(e) => e.target.style.display = 'none'} />
                <div>
                  <p className="font-semibold text-gray-800">პროექტის წამყვანი ორგანიზაცია:</p>
                  <p className="text-gray-700">ენობრივი ტექნოლოგიების პლატფორმა „ენაგრამი"</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <img src={`${ASSETS_URL}/images/tsu-logo.png`} alt="თსუ" className="w-16 h-auto" onError={(e) => e.target.style.display = 'none'} />
                <div>
                  <p className="font-semibold text-gray-800">პროექტის თანამონაწილე ორგანიზაცია:</p>
                  <p className="text-gray-700">ივანე ჯავახიშვილის სახელობის თბილისის სახელმწიფო უნივერსიტეტი, აფხაზეთის ინსტიტუტი</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <img src={`${ASSETS_URL}/images/rustaveli-logo.png`} alt="რუსთაველის ფონდი" className="w-24 h-auto" onError={(e) => e.target.style.display = 'none'} />
                <div>
                  <p className="text-gray-700">
                    პროექტი განხორციელდა <strong>შოთა რუსთაველის საქართველოს ეროვნული სამეცნიერო ფონდის</strong> ფინანსური მხარდაჭერით <strong>სახელმწიფო ენის დაცვისა და განვითარების ხელშემწყობ სამეცნიერო კვლევით საგრანტო კონკურსის ფარგლებში</strong> (გრანტის ნომერი: SL-24-218).
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Project Team */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 font-['bpg-caps']">პროექტის გუნდი</h2>
            <div className="space-y-3">
              <p className="text-gray-700">
                <strong>რატი სხირტლაძე — პროექტის ხელმძღვანელი,</strong> ენათმეცნიერების მიერ მოწოდებული მასალების დამუშავება და მონაცემთა ბაზაში განთავსება, დინამიური ვებსაიტის შექმნა.
              </p>
              <p className="text-gray-700">
                <strong>ლეილა ავიძბა — პროექტის თანახელმძღვანელი,</strong> აფხაზური ენის A1 დონის პროგრამის ზოგადი აღწერილობის შემუშავება, აფხაზურენოვანი მასალის შექმნა, საჭირო მასალის გახმოვანება.
              </p>
              <p className="text-gray-700">
                <strong>ნათია ფონიავა — ძირითადი პერსონალი,</strong> აფხაზური ენის A1 დონის პროგრამის ზოგადი აღწერილობის შემუშავება, გრამატიკული და თემატური მასალების მომზადება.
              </p>
              <p className="text-gray-700">
                <strong>ესმა კოკოსკერია — დამხმარე პერსონალი,</strong> აფხაზურენოვანი მასალის რედაქტირება, გახმოვანება და კორექტირება.
              </p>
              <p className="text-gray-700">
                <strong>ლევან ლაშაური</strong> — პროგრამისტი
              </p>
              <p className="text-gray-700 mt-4 italic">
                პროექტს კონსულტაციას უწევდნენ აფხაზური ენის აფხაზი სპეციალისტები სოხუმიდან.
              </p>
            </div>
          </section>

          {/* Abkhaz Language Section */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 font-['bpg-caps']">აფხაზური ენა</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              აფხაზური ენა აბაზურთან ერთად განეკუთვნება <strong>აფხაზურ-ადიღურ ენათა ჯგუფს</strong>, რომელშიც შედის ადიღეური (ჩერქეზული), ყაბარდოული და უბიხური ენებიც.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 font-['bpg-caps']">დიალექტები</h3>
            <p className="text-gray-700 mb-2">აფხაზურ ენაში ორი დიალექტი გამოიყოფა:</p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
              <li><strong>აბჟუური:</strong> უფრო მარტივი ბგერითი სისტემით, გავრცელებულია ოჩამჩირის რაიონში და საფუძვლად უდევს სალიტერატურო ენას.</li>
              <li><strong>ბზიფური:</strong> წარმოდგენილია გუდაუთისა და გაგრის რაიონებში.</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 font-['bpg-caps']">გავრცელების არეალი</h3>
            <p className="text-gray-700 mb-2">აფხაზური ენა გავრცელებულია:</p>
            <ul className="list-disc list-inside text-gray-700 mb-2 space-y-1 ml-4">
              <li>აფხაზეთის ავტონომიურ რესპუბლიკაში</li>
              <li>აჭარის ავტონომიურ რესპუბლიკაში (ბათუმი და მისი მიმდებარე ტერიტორია)</li>
            </ul>
            <p className="text-gray-700 mb-2 font-semibold">საზღვარგარეთ:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
              <li>თურქეთის რესპუბლიკაში (დუზჯე, ბოლუ, საკარია, კაისერი, სამსუნი და სხვ.)</li>
              <li>რუსეთის ფედერაციაში</li>
              <li>სირიის არაბთა რესპუბლიკაში</li>
              <li>იორდანიის ჰაშემიტურ სამეფოში</li>
            </ul>
          </section>

          {/* Alphabet History */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 font-['bpg-caps']">აფხაზური ანბანი</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              აფხაზური <strong>ახალდამწერლობიანი</strong> ენაა. მისი ანბანის შემუშავება მჭიდროდ უკავშირდება ამ ენის მეცნიერულად შესწავლის დაწყებას.
            </p>
            
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <tbody>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 p-3 font-semibold">1862 წელი</td>
                    <td className="border border-gray-300 p-3">პირველი ანბანი</td>
                    <td className="border border-gray-300 p-3">შეიქმნა გენერალ პეტრე უსლარის მიერ (კირილიცა + დამატებითი ნიშნები, მათ შორის ქართული: წ, ჭ, ჯ).</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 p-3 font-semibold">1926-1928 წწ.</td>
                    <td className="border border-gray-300 p-3">ლათინურ გრაფიკაზე გადასვლა</td>
                    <td className="border border-gray-300 p-3">აკადემიკოს ნიკო მარის ანბანი (შეიცავდა 74 ასო-ნიშანს).</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 p-3 font-semibold">1938-1954 წწ.</td>
                    <td className="border border-gray-300 p-3">ქართულ გრაფიკაზე გადასვლა</td>
                    <td className="border border-gray-300 p-3">დაემყარა მხედრულს (ავტორები: დიმიტრი გულია, სიმონ ჯანაშია, აკაკი შანიძე).</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 p-3 font-semibold">1954 წელი</td>
                    <td className="border border-gray-300 p-3">კირილიცაზე დაბრუნება</td>
                    <td className="border border-gray-300 p-3">პ. უსლარის ანბანის მ. ზავადსკისეული ვარიანტი</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 p-3 font-semibold">ამჟამად</td>
                    <td className="border border-gray-300 p-3">მოქმედი ანბანი</td>
                    <td className="border border-gray-300 p-3">ემყარება რუსულ გრაფიკას (კირილიცას). ანბანში 64 ასო-ნიშანია.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Bibliography */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 font-['bpg-caps']">გამოყენებული ლიტერატურა/ლექსიკონები</h2>
            <div className="text-gray-700 text-sm space-y-2">
              <p>ბერიძე, მარინა და სხვები. 2018. ქართული-აფხაზური-ინგლისური სიხშირული ლექსიკონი. Wojnowice: KEW.</p>
              <p>გვანცელაძე, თეიმურაზ. 2000. მცირე აფხაზურ-ქართულ ლექსიკონი. თბილისი: თბილისის უნივერსიტეტის გამომცემლობა.</p>
              <p>გვანცელაძე, თეიმურაზ. 2003. აფხაზური და ქართული ენების შემსწავლელთათვის. I. თბილისი: ქართული ენა.</p>
              <p>გვანცელაძე, თეიმურაზ. 2012. აფხაზურ-ქართულ ლექსიკონი. თბილისი: ინტელექტი.</p>
              <p>მაჭავარიანი, ნანა. 2018. აფხაზურ-ქართული ლექსიკონი ინდექსითა და გრამატიკული მიმოხილვით. თბილისი: თსუ გამომცემლობის სტამბა.</p>
              <p className="mt-4">Арсҭаа, Ш. 1996. <em>Аԥсуа бызшәа алитературатә бызшәа аграмматика</em>. Аҟуа: Алашара.</p>
              <p>Арсҭаа, Ш. Ҟ., Н. В. Аршба, В. Ҳ. Конџьариа, Л. П. Ҷкадуа, Р. К. Гәыблиа и Б. Гь. Џьонуа. 2014. <em>Аԥсуа грамматика</em> (I атом). Аҟәа: Аԥсны аҭҵаарадыррақәа ракадемиа Д. И. Гәлиа ихьӡ зху Аԥсуаҭҵааратә институт.</p>
              <p>Арсҭаа, Ш. К. 2021. <em>Асинтаксис (Аԥсуа грамматика: II атом)</em>. Аҟәа: Аԥсны аҭҵаарадыррақәа ракадемиа Д. И. Гәлиа ихьӡ зху Аԥсуаҭҵааратә институт.</p>
              <p><em>Аԥсуа бызшәа аҩышьаԥҟарақәа</em>. 1997. Аҟәа: Д. И. Гәлиа ихьӡ зху Аԥсуаҭҵааратә институт.</p>
              <p>Касландзия, Владимир. 2005. Абхазско-русский словарь I. А-Н. Сухум: ОЛМА ПРЕСС.</p>
              <p>Касландзия, Владимир. 2005a. Абхазско-русский словарь II. О-Џь. Сухум: ОЛМА-ПРЕСС.</p>
              <p>Касландзия, Владимир и Борис Джонуа. 2016. Русско-абхазский словарь I. Сухум: Дом печати.</p>
              <p>Касландзия, Владимир и Борис Джонуа. 2016а. Русско-абхазский словарь II. Сухум: Дом печати.</p>
              <p>Касландзия, Владимир и Борис Джонуа. 2016б. Русско-абхазский словарь III. Сухум: Дом печати.</p>
              <p>Шаҟрыл, Константин, Валентина Конџьариа и Лидия Ҷкадуа. 1987. <em>Aҧсуа бызшәа жәар II</em>. Аҟуа: Алашара.</p>
              <p>Шьаҟрыл, Константин и Валентина Конџьариа. 1986. <em>Aҧсуа бызшәа жәар I</em>. Аҟуа: Алашара.</p>
              <p className="mt-4"><em>Common European Framework Reference.</em> <a href="https://www.coe.int/en/web/common-european-framework-reference-languages/level-descriptions" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">https://www.coe.int/en/web/common-european-framework-reference-languages/level-descriptions</a></p>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default AboutPage;
